import React, { useEffect, useRef, useState } from 'react';
import { galleryImages } from '../data/mock';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Gallery = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const openLightbox = (index) => {
    setSelectedImage(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImage === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  return (
    <>
      <section
        id="gallery"
        ref={sectionRef}
        className="bg-[#0d0b09] py-16 md:py-24 lg:py-32 px-4 md:px-6 overflow-hidden"
      >
        <div className="container mx-auto max-w-6xl">
          {/* Section Header */}
          <div
            className={`text-center mb-16 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Divider */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
              <div className="mx-4 w-2 h-2 rotate-45 border border-[#D4A853]" />
              <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
            </div>

            <h2 className="text-[#A69080] text-2xl md:text-3xl tracking-[0.3em] uppercase mb-4">
              Gallery
            </h2>

            {/* Divider */}
            <div className="flex items-center justify-center mt-8">
              <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
              <div className="mx-4 w-2 h-2 rotate-45 border border-[#8B0000]" />
              <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
            </div>

            <p className="text-[#8B7355] mt-8 text-base md:text-lg">
              A glimpse into my world of darkness and desire.
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={image.id}
                className={`relative group cursor-pointer overflow-hidden aspect-square transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908] via-transparent to-transparent opacity-60" />
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-[#D4A853]/0 group-hover:bg-[#D4A853]/20 transition-all duration-500" />
                {/* Border on hover */}
                <div className="absolute inset-2 border border-[#D4A853]/0 group-hover:border-[#D4A853]/50 transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 z-50 bg-[#0a0908]/98 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            className="absolute top-6 right-6 text-[#8B7355] hover:text-[#D4A853] transition-colors z-10"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Navigation */}
          <button
            className="absolute left-4 md:left-8 text-[#8B7355] hover:text-[#D4A853] transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          <button
            className="absolute right-4 md:right-8 text-[#8B7355] hover:text-[#D4A853] transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].alt}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>

          {/* Image Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#8B7355] text-sm tracking-widest">
            {selectedImage + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </>
  );
};

export default Gallery;
